// src/pages/qc/CoolantAnalysisModal.jsx
import { useState, useMemo } from "react";
import { todayDubai } from "../../utils/dateUtils";

const TODAY = todayDubai();

function num(v) { return v === "" ? null : parseFloat(v); }

function specHint(min, max, unit) {
  if (min == null && max == null) return null;
  const r = min != null && max != null
    ? `${min} – ${max}`
    : min != null ? `≥ ${min}` : `≤ ${max}`;
  return `spec: ${r}${unit ? " " + unit : ""}`;
}

function Row({ label, children }) {
  return (
    <div className="form-row">
      <span className="form-label">{label}</span>
      <div style={{ display:"flex", gap:6, alignItems:"center", flex:1 }}>{children}</div>
    </div>
  );
}

function NumInput({ value, onChange, placeholder, width = 120 }) {
  return (
    <input type="number" className="form-input" step="any" value={value}
      onChange={e => onChange(e.target.value)} placeholder={placeholder} style={{ width }} />
  );
}

export function CoolantAnalysisModal({ specs, onSave, onCancel }) {
  const [form, setForm] = useState({
    date: TODAY, productId: "", batchNo: "",
    appearance: "", color: "",
    density: "", reserveAlkalinity: "", ph: "", freezingPt: "",
    remarks: "",
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  const spec = useMemo(
    () => specs.find(s => s.productId === form.productId) ?? null,
    [specs, form.productId]
  );

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.productId) { setError("Product is required."); return; }
    setSaving(true); setError(null);
    try {
      await onSave({
        date:              form.date,
        productId:         form.productId,
        batchNo:           form.batchNo.trim() || null,
        appearance:        form.appearance.trim() || null,
        color:             form.color.trim() || null,
        density:           num(form.density),
        reserveAlkalinity: num(form.reserveAlkalinity),
        ph:                num(form.ph),
        freezingPt:        num(form.freezingPt),
        remarks:           form.remarks.trim() || null,
      });
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  const sorted = [...specs].sort((a, b) => a.productId.localeCompare(b.productId));
  const s = spec;

  return (
    <div className="modal wide">
      <div className="modal-header">
        <h3 className="modal-title">Add Coolant Analysis</h3>
        <button className="btn" onClick={onCancel}>✕</button>
      </div>

      <form onSubmit={handleSubmit}>
        <Row label="Date">
          <input type="date" className="form-input" value={form.date}
            onChange={e => set("date", e.target.value)} required style={{ width:160 }} />
        </Row>

        <Row label="Product *">
          <select className="form-select" value={form.productId}
            onChange={e => set("productId", e.target.value)} style={{ flex:1 }}>
            <option value="">— select product —</option>
            {sorted.map(sp => (
              <option key={sp.productId} value={sp.productId}>{sp.productId}</option>
            ))}
          </select>
        </Row>

        <Row label="Batch No">
          <input className="form-input" placeholder="e.g. B-2501" value={form.batchNo}
            onChange={e => set("batchNo", e.target.value)} style={{ flex:1 }} />
        </Row>

        <div className="form-sep" />

        <Row label="Appearance">
          <input className="form-input" value={form.appearance}
            onChange={e => set("appearance", e.target.value)}
            placeholder={s?.appearance ?? "e.g. Clear & Bright"} style={{ flex:1 }} />
          {s?.appearance && <span style={{ fontSize:11, color:"#aaa" }}>spec: {s.appearance}</span>}
        </Row>

        <Row label="Color">
          <input className="form-input" value={form.color}
            onChange={e => set("color", e.target.value)}
            placeholder={s?.color ?? "e.g. Blue"} style={{ flex:1 }} />
          {s?.color && <span style={{ fontSize:11, color:"#aaa" }}>spec: {s.color}</span>}
        </Row>

        <Row label="Density @ 20°C (g/cc)">
          <NumInput value={form.density} onChange={v => set("density", v)}
            placeholder={s ? `${s.densityMin ?? ""}–${s.densityMax ?? ""}` : "value"} />
          {s && <span style={{ fontSize:11, color:"#aaa" }}>{specHint(s.densityMin, s.densityMax, "g/cc")}</span>}
        </Row>

        <Row label="Reserve Alkalinity (mL HCl 0.1N)">
          <NumInput value={form.reserveAlkalinity} onChange={v => set("reserveAlkalinity", v)}
            placeholder={s ? `${s.reserveAlkalinityMin ?? ""}–${s.reserveAlkalinityMax ?? ""}` : "value"} />
          {s && <span style={{ fontSize:11, color:"#aaa" }}>{specHint(s.reserveAlkalinityMin, s.reserveAlkalinityMax)}</span>}
        </Row>

        <Row label="pH">
          <NumInput value={form.ph} onChange={v => set("ph", v)}
            placeholder={s ? `${s.phMin ?? ""}–${s.phMax ?? ""}` : "value"} width={90} />
          {s && <span style={{ fontSize:11, color:"#aaa" }}>{specHint(s.phMin, s.phMax)}</span>}
        </Row>

        <Row label="Freezing Point (°C)">
          <NumInput value={form.freezingPt} onChange={v => set("freezingPt", v)}
            placeholder={s?.freezingPtMax != null ? `≤ ${s.freezingPtMax}` : "value"} width={100} />
          {s?.freezingPtMax != null && (
            <span style={{ fontSize:11, color:"#aaa" }}>spec: ≤ {s.freezingPtMax}°C</span>
          )}
        </Row>

        <div className="form-sep" />

        <Row label="Remarks">
          <textarea className="form-input" rows={2} value={form.remarks}
            onChange={e => set("remarks", e.target.value)}
            placeholder="Optional notes…" style={{ flex:1, resize:"vertical" }} />
        </Row>

        {error && <div className="alert-warn" style={{ marginTop:8 }}>{error}</div>}

        <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:16 }}>
          <button type="button" className="btn" onClick={onCancel} disabled={saving}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}
